import { useMemo, useSyncExternalStore } from "react";

/** Nothing ever changes the answer, so the store never notifies. */
const subscribeNever = () => () => {};

/**
 * A value only the browser can know, read once after hydration.
 *
 * The server render and the first client render both return `serverValue`, so
 * the hydrated markup matches; the browser answer arrives with hydration
 * instead of through a `setState` in an effect, which costs an extra render
 * and is what `react-hooks/set-state-in-effect` objects to.
 *
 * `read` must be a stable function — a module-level one, or a memoised one.
 */
export function useClientValue<T>(read: () => T, serverValue: T): T {
  const getSnapshot = useMemo(() => {
    // `useSyncExternalStore` calls the getter on every render and compares by
    // identity, so the browser answer is read once and then held.
    let cached: { value: T } | null = null;
    return () => (cached ??= { value: read() }).value;
  }, [read]);
  return useSyncExternalStore(subscribeNever, getSnapshot, () => serverValue);
}
